/**
 * Table of contents — pipeline stages S17 and S18.
 *
 * Spec: docs/spec/00-MASTER-SPEC.md §3.12 (R-308 … R-315).
 * Source: latex2html-french-trad/src/lib/.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW THE TOC IS ACTUALLY FED
 *
 * Every heading this converter emits carries the class `no-toc` — `\section` and
 * friends at, `\maketitle` at, `\afficherTitre` at  — and
 *  skips any heading whose attributes contain that string. So the visible
 * table of contents is built EXCLUSIVELY from `\addcontentsline{toc}{…}{…}`,
 * which  turns into an invisible marker span:
 *
 *     <span class="manual-toc-entry" data-level="N" data-title="…"></span>
 *
 * `no-toc` and `manual-toc-entry` are the only two classes in the whole engine
 * emitted with NO CSS rule behind them (G-31). They are pure control markers and
 * MUST still be emitted: drop `no-toc` and every heading floods the TOC; drop
 * `manual-toc-entry` and the TOC empties. Neither is decorative.
 *
 * The headings themselves get their `id` here, in pass 1 — matched to a manual
 * entry by comparing normalised titles.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * BUG-FOR-BUG (CLAUDE.md §3). Reproduced deliberately, pinned in toc.test.ts:
 *   RK-29  Neither collector regex carries the `s` flag, so a multi-line
 *          `\section{Titre\nDeux}` is invisible to them and never receives an
 *          `id`. `[^"]*` in the manual-entry branch DOES span newlines, so the
 *          TOC entry is still created — a link to an anchor that does not exist.
 *   RK-16  `buildHierarchy` silently discards entries above `minLevel` that appear
 *          before any `minLevel` entry, so a document whose only entry is a
 *          `subsection` renders a visible-but-empty `<nav>`.
 *   R-311  `generateHeadingId` strips through the ASCII-only `\w`, so accents are
 *          DELETED rather than folded (`Première` → `premire`) and HTML entities
 *          leak their letters into the slug (`&nbsp;` → `nbsp`).
 *   R-314  The empty-TOC placeholder text is hard-coded English in a French
 *          document ("No headings found").
 *   NEW    `assignedIds` is keyed by title, so two headings sharing a title
 *          overwrite each other and every TOC entry for that title points at the
 *          LAST id. See toc.test.ts "duplicate titles".
 *
 * Pure: no I/O, no DOM, no module-level mutable state. The source's four
 * `console.log` diagnostics (, 1864, 1884, 1887) are omitted — they never
 * touch the returned `{ html, toc }`.
 *
 * VERIFIED AGAINST THE ORIGINAL, not against a reading of it:
 *   • ~15,000 generated documents across four `tocDepth` values, byte-identical
 *     in both `html` and `toc`.
 *   • Corpus replay: for all 74 fixtures × 2 modes, the original's html was
 *     captured just before S17 and this stage re-run on it — identical html, toc
 *     and post-substitution output.
 */

/** One node of the rendered TOC tree. */
export interface TocEntry {
  level: number
  id: string
  title: string
  children: TocEntry[]
}

/** A collected heading or manual entry, in document order. */
interface CollectedHeading {
  level: number
  title: string
  index: number
  isManual?: boolean
}

/**
 * R-311 — slugify a heading title.
 *
 * DEFECT, preserved: `[^\w\s-]` is ASCII-only, so `é`, `í`, `á`, `œ` are DELETED,
 * not transliterated — `Première partie` becomes `premire-partie`, a misspelling,
 * not merely an ASCII fold. HTML entities are stripped of their punctuation and
 * their letters survive into the slug: `&nbsp;` → `nbsp`, `&amp;` → `amp`,
 * `&quot;` → `quot`. `_` survives because `\w` includes it.
 *
 * The `index` is appended unconditionally, so ids are unique per call site even
 * when two titles slugify identically.
 */
export function generateHeadingId(text: string, index: number): string {
  const clean = text
    .replace(/<[^>]+>/g, '') // R-311: remove HTML tags
    .replace(/[^\w\s-]/g, '') // R-311: ASCII-only \w — accents are deleted here
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-') // R-311: runs of whitespace (incl. \n, \t) → one hyphen

  return `${clean || 'section'}-${index}`
}

/**
 * Normalise a title for heading↔manual-entry comparison.
 *
 * Only three entities are decoded, and only `<tag>` markup is dropped — which is
 * what lets `<h1 class="no-toc"><span class="hfill"></span>Titre</h1>` match an
 * `\addcontentsline` that wrote plain `Titre`.
 */
function normalizeTitle(title: string): string {
  return title
    .replace(/<[^>]+>/g, '')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .trim()
}

/**
 * S17 (R-308 … R-314, ) — collect headings, inject anchors, render the
 * `<nav>`.
 *
 * @param html      the document after S16.
 * @param tocDepth  from `\setcounter{tocdepth}{n}`; the coordinator passes
 *                  `settings.tocDepth ?? 4`.
 * @returns `html` with anchors injected and marker spans deleted, plus the
 *          rendered `toc` — which S18 then substitutes into every placeholder.
 */
export function buildTableOfContents(
  html: string,
  tocDepth: number = 4,
): { html: string; toc: string } {
  // R-308 — VERBATIM. One alternation over real headings and manual
  // markers, so both land in `headings` in document order. NO `s` flag: `(.*?)`
  // cannot cross a newline, so a multi-line heading is invisible (RK-29). The
  // manual branch's `([^"]*)` is a character class and DOES cross newlines, which
  // is why the entry survives while its anchor does not.
  const headingRegex =
    /<h([1-4])([^>]*)>(.*?)<\/h\1>|<span class="manual-toc-entry" data-level="(\d+)" data-title="([^"]*)"><\/span>/g

  const headings: CollectedHeading[] = []
  let match: RegExpExecArray | null
  let index = 0

  while ((match = headingRegex.exec(html)) !== null) {
    if (match[1]) {
      // R-309 — every heading the converter emits carries `no-toc`, so
      // in practice this `continue` fires for all of them and only raw `<h1>`
      // typed by a translator (nothing is HTML-escaped — DF-9) gets through.
      // `index` is NOT incremented for a skipped heading.
      if (match[2] && match[2].includes('no-toc')) continue

      const level = parseInt(match[1], 10)
      const title = match[3] ?? ''
      headings.push({ level, title, index: index++ })
    } else if (match[4]) {
      const level = parseInt(match[4], 10)
      const title = match[5] ?? ''
      headings.push({ level, title, index: index++, isManual: true })
    }
  }

  let modifiedHtml = html

  // the set of titles an \addcontentsline asked for.
  const manualEntryTitles = new Set<string>()
  for (const h of headings) {
    if (h.isManual) manualEntryTitles.add(h.title)
  }

  //. NOTE: `assignedIds` is keyed by TITLE, not by position. Two
  // headings with the same text overwrite each other and the TOC links both to
  // the last one. `headingIndex` is a second, independent counter — it is not the
  // `index` used above.
  const assignedIds = new Map<string, string>()
  let headingIndex = 0

  // R-310 — pass 1: give an `id` to every heading whose normalised
  // title matches a manual entry. Same regex, same missing `s` flag (RK-29).
  modifiedHtml = modifiedHtml.replace(
    /<h([1-4])([^>]*)>(.*?)<\/h\1>/g,
    (whole: string, hLevel: string, hAttrs: string, hTitle: string): string => {
      const normalizedHeading = normalizeTitle(hTitle)

      for (const manualTitle of manualEntryTitles) {
        if (normalizedHeading === normalizeTitle(manualTitle)) {
          // The id is generated from the RAW heading title, not the normalised
          // one — so `A&nbsp;B` slugifies to `anbspb`, entity letters and all.
          const id = generateHeadingId(hTitle, headingIndex++)
          assignedIds.set(hTitle, id)
          assignedIds.set(manualTitle, id)
          return `<h${hLevel}${hAttrs || ''} id="${id}">${hTitle}</h${hLevel}>`
        }
      }

      return whole
    },
  )

  // R-312 — pass 2: delete every marker span. A span that found no
  // heading in pass 1 falls back to putting its id on the nearest PRECEDING
  // page-break divider; if there is none, the span is deleted anyway and the TOC
  // link is dead.
  //
  // The loop mutates `modifiedHtml` while iterating it with a stateful global
  // regex, driving `lastIndex` by hand. Reproduced exactly — the traversal order
  // is observable (see the two-entries/one-divider test).
  const manualEntryRegex =
    /<span class="manual-toc-entry" data-level="(\d+)" data-title="([^"]*)"><\/span>/g
  let manualMatch: RegExpExecArray | null
  while ((manualMatch = manualEntryRegex.exec(modifiedHtml)) !== null) {
    const mTitle = manualMatch[2] ?? ''
    const decodedTitle = mTitle.replace(/&quot;/g, '"').replace(/&amp;/g, '&')
    let id = assignedIds.get(mTitle) ?? assignedIds.get(decodedTitle)

    if (id) {
      // Matched in pass 1 — the heading already carries the id; drop the marker.
      modifiedHtml =
        modifiedHtml.substring(0, manualMatch.index) +
        modifiedHtml.substring(manualMatch.index + manualMatch[0].length)
      manualEntryRegex.lastIndex = manualMatch.index
    } else {
      id = generateHeadingId(decodedTitle, headingIndex++)
      assignedIds.set(mTitle, id)
      assignedIds.set(decodedTitle, id)

      const textBefore = modifiedHtml.substring(0, manualMatch.index)
      // Greedy `.*` with `s` → the LAST page-break divider before this marker.
      // The literal `<div class="page-break"` means a divider that already
      // received an id is not matched, and therefore never reused.
      const pageBreakMatch = textBefore.match(/.*(<div class="page-break"[^>]*>)/s)

      const pageBreakTag = pageBreakMatch?.[1]
      if (pageBreakMatch && pageBreakTag) {
        const pageBreakPos = textBefore.lastIndexOf(pageBreakTag)
        const newPageBreakTag = pageBreakTag.replace(
          '<div class="page-break"',
          `<div id="${id}" class="page-break"`,
        )
        modifiedHtml =
          modifiedHtml.substring(0, pageBreakPos) +
          newPageBreakTag +
          modifiedHtml.substring(pageBreakPos + pageBreakTag.length)
      }
      // else: no divider — the span is still removed and the link stays dead.

      // Re-find the span: the page-break rewrite above shifted every later index.
      // `indexOf` takes the FIRST occurrence, which for two identical markers is
      // not necessarily the one just matched.
      const newIndex = modifiedHtml.indexOf(manualMatch[0])
      if (newIndex !== -1) {
        modifiedHtml =
          modifiedHtml.substring(0, newIndex) +
          modifiedHtml.substring(newIndex + manualMatch[0].length)
      }
      manualEntryRegex.lastIndex = 0
    }
  }

  // R-313 — nest by level.
  const buildHierarchy = (items: CollectedHeading[], minLevel: number = 1): TocEntry[] => {
    const result: TocEntry[] = []
    let i = 0

    while (i < items.length) {
      const item = items[i]
      if (item === undefined) break // unreachable: i < items.length

      if (item.level < minLevel) {
        break // gone back up a level — the caller resumes here
      }

      if (item.level === minLevel) {
        const decodedTitle = item.title.replace(/&quot;/g, '"').replace(/&amp;/g, '&')
        // Falls back to a freshly generated id when nothing was assigned — this is
        // the branch that produces a dead link for an unmatched heading, and it
        // uses `item.index` (collection order), not `headingIndex`.
        const id =
          assignedIds.get(item.title) ??
          assignedIds.get(decodedTitle) ??
          generateHeadingId(item.title, item.index)

        const entry: TocEntry = { level: item.level, id, title: item.title, children: [] }

        let j = i + 1
        const children: CollectedHeading[] = []
        while (j < items.length) {
          const next = items[j]
          if (next === undefined || next.level <= minLevel) break
          children.push(next)
          j++
        }

        if (children.length > 0) {
          entry.children = buildHierarchy(children, minLevel + 1)
        }

        result.push(entry)
        i = j
      } else {
        // DEFECT RK-16, preserved: an entry deeper than `minLevel` that has no
        // `minLevel` parent before it is silently dropped.
        i++
      }
    }

    return result
  }

  const tocTree = buildHierarchy(headings)

  // R-314 — render. Indentation and newlines are part of the
  // contract; the goldens are compared byte-for-byte.
  const renderTocLevel = (entries: TocEntry[], currentDepth: number = 1): string => {
    // PREAMBLE LINK: \setcounter{tocdepth}{4} (preambuleV3.tex:201) — "Présenter quatre niveaux de
    // titre (section, subsection, subsubsection et paragraph)". READ AND HONOURED: settings.ts
    // R-039 extracts it and index.ts passes it in as `tocDepth`, which is the bound tested here
    // and on the recursion below. This is one of the few preamble lines that genuinely drives
    // behaviour. Since 2026-09-07 index.ts applies `?? 4`, so a document asking for 0 reaches this
    // guard and gets the empty nav it already rendered correctly.
    if (entries.length === 0 || currentDepth > tocDepth) return ''

    let out = '<ul class="toc-list">\n'
    for (const entry of entries) {
      out += `  <li class="toc-item toc-level-${entry.level}">\n`
      out += `    <a href="#${entry.id}" class="toc-link">${entry.title}</a>\n`

      if (entry.children.length > 0 && currentDepth < tocDepth) {
        out += renderTocLevel(entry.children, currentDepth + 1)
      }

      out += `  </li>\n`
    }
    out += '</ul>\n'
    return out
  }

  // R-314. The wrapper is chosen on `headings.length`, NOT on
  // whether anything rendered — so a document whose only entry was discarded by
  // `buildHierarchy` gets the visible `<nav class="table-of-contents">\n</nav>`
  // with no list inside it (RK-16), not the hidden variant.
  // "No headings found" is hard-coded English in a French document.
  // PREAMBLE LINK: \addto\captionsfrench{\renewcommand{\contentsname}{}} (preambuleV3.tex:238) —
  // "Masquer le titre Table des matières". NOT READ; commands-text.ts R-180 merely deletes the
  // token. The request is satisfied by hard-coding: neither branch below emits a heading above the
  // list, so the TOC has no title to hide. The output is right and the causal link is absent — if
  // a title is ever added here, that preamble line must be honoured or its CONVERTISSEUR note
  // corrected. /!\ The empty-TOC branch does emit hard-coded ENGLISH text in a French document.
  const tocHtml =
    headings.length > 0
      ? `<nav class="table-of-contents">\n${renderTocLevel(tocTree)}</nav>`
      : '<nav class="table-of-contents toc-hidden"><p class="toc-empty">No headings found</p></nav>'

  return { html: modifiedHtml, toc: tocHtml }
}

/**
 * S18 (R-315, ) — substitute the rendered TOC into every placeholder.
 *
 * The placeholder is emitted by `\tableofcontents` and by the Mode-B
 * `\afficherSommaire{}` handler. Every occurrence receives its own copy;
 * the `<nav>` carries no id, so duplicating it produces no duplicate-id problem —
 * but every anchor inside is duplicated, and browsers resolve `#id` to the first.
 */
export function insertTableOfContents(html: string, toc: string): string {
  // R-315. `toc` is inserted through a replacement STRING, so `$&`,
  // "$'", "$`" and `$$` inside it ARE expanded (verified against the original).
  // Latent, not active: every `$` is consumed by S9/S10 well before
  // \addcontentsline runs at, so no `$` reaches a TOC title.
  return html.replace(/<nav class="toc-placeholder">\[Table of Contents\]<\/nav>/g, toc)
}
